import { useEffect } from 'react'
import { StyledText, TooltipWrapper, Wrapper } from './styles'
import RadioInput from './input'
import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import InfoTooltip from 'components/shared/InfoTooltip'

export interface RadioOption {
    option: string
    tooltipMessage?: string
    id: string
    description?: string
    additionalDescription?: JSX.Element
}

interface RadioProps {
    options: RadioOption[]
    direction?: 'row' | 'column'
    value: string
    setValue: (value: string) => void
    isDisabled?: boolean
    isTooltipPrimary?: boolean
    isTooltipTop?: boolean
    testIdPrefix?: string
    isFullWidth?: boolean
}

const Radio = ({
    options,
    direction = 'row',
    value,
    setValue,
    isDisabled,
    isTooltipPrimary = false,
    isTooltipTop = false,
    testIdPrefix,
    isFullWidth,
}: RadioProps) => {
    useEffect(() => {
        if (!value) setValue(options[0].id)
    }, [])

    return (
        <Wrapper>
            <Box direction={direction} gap={[16, 40]} isFullWidth={isFullWidth}>
                {options.map((item, index) => {
                    const {
                        option,
                        tooltipMessage,
                        id,
                        description,
                        additionalDescription,
                    } = item
                    return (
                        <Box
                            direction="row"
                            align="flex-start"
                            gap={12}
                            key={id}
                            onClick={(event) => {
                                if (isDisabled) return
                                event.preventDefault()
                                setValue(id)
                            }}
                            data-testid={`${
                                testIdPrefix ? `${testIdPrefix}-` : ''
                            }check-${id}`}
                            isFullWidth={isFullWidth}
                        >
                            <RadioInput
                                id={`${id}${index}`}
                                option={option}
                                isChecked={value === id}
                            />
                            <Box gap={8}>
                                <StyledText
                                    as="label"
                                    variant="body16"
                                    htmlFor={`${id}${index}`}
                                    hasTransition
                                    color="grayscale_900"
                                >
                                    {option}
                                    {!!tooltipMessage && (
                                        <TooltipWrapper>
                                            <InfoTooltip
                                                isLarge={isTooltipTop}
                                                message={tooltipMessage}
                                                color={
                                                    isTooltipPrimary
                                                        ? 'primary_500'
                                                        : 'secondary_500'
                                                }
                                            />
                                        </TooltipWrapper>
                                    )}
                                </StyledText>
                                {!!description && (
                                    <Text
                                        variant="body"
                                        color={
                                            value === id
                                                ? 'grayscale_700'
                                                : 'grayscale_500'
                                        }
                                    >
                                        {description}
                                    </Text>
                                )}
                                {additionalDescription}
                            </Box>
                        </Box>
                    )
                })}
            </Box>
        </Wrapper>
    )
}

export default Radio
